import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AlertTriangle } from 'lucide-react';
import { Button } from './Button';
import { Input } from './Inputs';
import { Modal } from './Dialog';

export interface ConfirmOptions {
  title: string;
  description?: ReactNode;
  message?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'primary' | 'danger';
  confirmTone?: 'primary' | 'danger';
  /** The user must type this exact text before the confirm button enables (irreversible actions). */
  requireText?: string;
}

type Pending = ConfirmOptions & { resolve: (ok: boolean) => void };

const ConfirmContext = createContext<((options: ConfirmOptions) => Promise<boolean>) | null>(null);

/**
 * Promise-based confirmation, so callers read top-to-bottom:
 *   const confirm = useConfirm();
 *   if (!(await confirm({ title: 'Delete record?', tone: 'danger' }))) return;
 */
export function ConfirmProvider({ children }: { children: ReactNode }) {
  const [pending, setPending] = useState<Pending | null>(null);
  const [typed, setTyped] = useState('');
  const resolver = useRef<((ok: boolean) => void) | null>(null);
  const textRef = useRef<HTMLInputElement>(null);
  const needsText = Boolean(pending?.requireText);
  useEffect(() => {
    if (needsText) {
      textRef.current?.focus();
    }
  }, [needsText]);

  const confirm = useCallback((options: ConfirmOptions) => {
    // A second confirm while one is open cancels the first rather than leaking its promise.
    resolver.current?.(false);
    return new Promise<boolean>((resolve) => {
      resolver.current = resolve;
      setTyped('');
      setPending({ ...options, resolve });
    });
  }, []);

  const settle = (ok: boolean): void => {
    pending?.resolve(ok);
    resolver.current = null;
    setPending(null);
  };

  const value = useMemo(() => confirm, [confirm]);
  const danger = pending?.tone === 'danger' || pending?.confirmTone === 'danger';
  const blocked = Boolean(pending?.requireText) && typed !== pending?.requireText;

  return (
    <ConfirmContext.Provider value={value}>
      {children}
      <Modal
        open={pending !== null}
        onClose={() => settle(false)}
        size="sm"
        title={pending?.title ?? ''}
        description={pending?.description ?? pending?.message}
        footer={
          <>
            <Button variant="secondary" onClick={() => settle(false)}>
              {pending?.cancelLabel ?? 'Cancel'}
            </Button>
            <Button variant={danger ? 'danger' : 'primary'} disabled={blocked} onClick={() => settle(true)}>
              {pending?.confirmLabel ?? 'Confirm'}
            </Button>
          </>
        }
        headerAside={danger ? <AlertTriangle className="h-5 w-5 text-danger-600" aria-hidden="true" /> : undefined}
      >
        {pending?.requireText ? (
          <div className="space-y-2">
            <label htmlFor="confirm-text" className="text-sm text-ink-muted">
              Type <strong className="font-mono text-ink">{pending.requireText}</strong> to confirm.
            </label>
            <Input id="confirm-text" ref={textRef} value={typed} onChange={(e) => setTyped(e.target.value)} autoComplete="off" />
          </div>
        ) : (
          <p className="sr-only">Choose Confirm to continue or Cancel to go back.</p>
        )}
      </Modal>
    </ConfirmContext.Provider>
  );
}

export function useConfirm(): (options: ConfirmOptions) => Promise<boolean> {
  const confirm = useContext(ConfirmContext);
  if (!confirm) {
    throw new Error('useConfirm must be used inside <ConfirmProvider>');
  }
  return confirm;
}

/** Controlled variant for places that want to render the dialog themselves. */
export function ConfirmDialog({
  open,
  onConfirm,
  onCancel,
  title,
  description,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'primary',
  loading,
}: ConfirmOptions & { open: boolean; onConfirm: () => void; onCancel: () => void; loading?: boolean }) {
  return (
    <Modal
      open={open}
      onClose={onCancel}
      size="sm"
      title={title}
      description={description}
      dismissible={!loading}
      footer={
        <>
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            {cancelLabel}
          </Button>
          <Button variant={tone === 'danger' ? 'danger' : 'primary'} onClick={onConfirm} loading={loading}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p className="sr-only">Choose {confirmLabel} to continue or {cancelLabel} to go back.</p>
    </Modal>
  );
}
